/**
 * IT / Counter Validation page at /it/counter-validation.
 *
 * Validates the Cherry Creek 1173 people counter ONE ARRIVAL AT A TIME. Each row is
 * the people who came through the door together: when they crossed, which way the
 * counter decided they went, and one video of that moment.
 *
 * GROUPED, because a family is not three unrelated events. The counter sends no group
 * id, so people are grouped by going the same way within two seconds of each other,
 * which turned 916 crossings into 566 arrivals on 2026-08-04. A group of several gets
 * a disclosure arrow that lists the individuals. Steven, 2026-08-05: "we group those
 * rows together to indicate they walked in at the same time".
 *
 * Nothing plays until it is asked for. Clips are a few MB each and a trading day
 * is hundreds of them, so the page used to try to load a day of video at once and
 * took far too long to open. Each row now has a Play button that fetches one
 * signed URL and shows one player, and there is one player per arrival rather than
 * per person, because a 12-second pad makes the individual clips near-duplicates.
 *
 * Columns: time, what the counter decided, the adult and child split, and what it
 * determined about the people (height, gender, age band, staff badge), then the person
 * ids and the footage. Every one of those person columns shows a dash when the counter
 * did not determine it, which is currently always the case for height, gender and age;
 * see the note on person_height in useStoreTrafficValidation.
 *
 * THE ADULT AND CHILD SPLIT IS PER MINUTE, NOT PER ARRIVAL, and the column says so.
 * The counter states it per reporting window and 59% of windows hold more than one
 * arrival, so it cannot be attributed to one group inside a window. Where an arrival is
 * the only one of its direction in its minute the figure does describe exactly those
 * people, and the cell reads normally instead of dimmed.
 *
 * Controls: date picker (defaults today), store label (hardcoded Cherry Creek
 * 1173), a direction filter that DEFAULTS to counted crossings only, and an
 * "Outside store hours only" toggle driven by the STORE RECORD rather than by any
 * constant here, so it stays correct as the report grows past one store. The
 * summary strip ignores the direction filter and always describes the whole day, so
 * the walked-past and turned-around counts stay visible as numbers even when those
 * rows are out of the table.
 *
 * Data: GET /v1/store-traffic/validation?date=<selected> for the listing, then
 * GET /v1/store-traffic/validation/clip?key=<key> per click.
 *
 * Back link top-left per cockpit-ui-navigation pattern.
 */

import { useMemo, useState } from 'react';
import {
  Camera,
  CameraOff,
  ChevronDown,
  ChevronRight,
  Loader2,
  Play,
  X,
} from 'lucide-react';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import {
  CROSSING,
  ageLabel,
  crossingLabel,
  fetchClipUrl,
  genderLabel,
  groupArrivals,
  isOutsideStoreHours,
  staffLabel,
  summarizeAttribute,
  todayDateLocal,
  useStoreTrafficValidation,
  type ArrivalGroup,
  type StoreHours,
  type TrafficValidationRow,
} from '@/lib/useStoreTrafficValidation';

const STORE_LABEL = 'Cherry Creek 1173';

/**
 * Filter options, most-consequential first per the cockpit filter convention.
 *
 * `counted` is the DEFAULT (Steven, 2026-08-03: "change the report so it doesn't
 * have the walk bys"). People who walked past or turned around are still stored
 * and still reachable from this dropdown, because they are the evidence of
 * undercounting, but they are noise in the day's report and they crowd out the
 * rows that carry a count.
 */
const DIRECTION_FILTERS = [
  { value: 'counted', label: 'Counted (entered or exited)' },
  { value: String(CROSSING.IN), label: 'Entered' },
  { value: String(CROSSING.OUT), label: 'Exited' },
  { value: 'uncounted', label: 'Not counted (walked past or turned around)' },
  { value: 'all', label: 'Everyone the counter saw' },
] as const;

const DEFAULT_DIRECTION = 'counted';

function matchesDirection(row: TrafficValidationRow, filter: string): boolean {
  if (filter === 'all') return true;
  if (filter === 'counted') return row.traffic_in > 0 || row.traffic_out > 0;
  if (filter === 'uncounted') {
    return row.event_type === CROSSING.PASSBY || row.event_type === CROSSING.TURNBACK;
  }
  return String(row.event_type) === filter;
}

/**
 * The day's totals. Computed from every person the counter saw, INDEPENDENT of the
 * direction filter, so the walked-past and turned-around counts stay visible as
 * numbers even though those rows are filtered out of the table below. Those two
 * figures are the accuracy signal worth seeing at a glance: they say how many
 * people the counter noticed and decided not to count.
 */
function SummaryStrip({
  rows,
  groups,
}: {
  rows: TrafficValidationRow[];
  groups: ArrivalGroup[];
}) {
  const totalIn = rows.reduce((s, r) => s + r.traffic_in, 0);
  const totalOut = rows.reduce((s, r) => s + r.traffic_out, 0);
  const walkedPast = rows.filter((r) => r.event_type === CROSSING.PASSBY).length;
  const turnedAround = rows.filter((r) => r.event_type === CROSSING.TURNBACK).length;
  // Counted per ARRIVAL, not per person. The capture service films one clip per
  // arrival, so the other people in a group have no clip of their own by design and
  // counting them as missing would report a fault that does not exist.
  const missingFootage = groups.filter((g) => !g.has_footage).length;

  // Adults and children entering, summed over DISTINCT reporting windows. The counter
  // states these per window and every person in a window carries the same pair, so
  // summing the rows would multiply each window by its own headcount. Steven asked to
  // see these on the page (2026-08-05); they are the only adult and child figures the
  // counter gives us, and they are exact for the day even though they cannot be
  // attributed to an individual.
  const byWindow = new Map<string, { adults: number; children: number }>();
  for (const r of rows) {
    if (r.window_time === null) continue;
    if (r.window_in_adult === null && r.window_in_child === null) continue;
    byWindow.set(r.window_time, {
      adults: r.window_in_adult ?? 0,
      children: r.window_in_child ?? 0,
    });
  }
  const windows = [...byWindow.values()];
  const adultsIn = windows.reduce((s, w) => s + w.adults, 0);
  const childrenIn = windows.reduce((s, w) => s + w.children, 0);
  const haveAgeSplit = windows.length > 0;

  return (
    <div className="mb-4 flex flex-wrap gap-3">
      {[
        { label: 'Entered', value: totalIn.toLocaleString() },
        { label: 'Exited', value: totalOut.toLocaleString() },
        ...(haveAgeSplit
          ? [
              { label: 'Adults in', value: adultsIn.toLocaleString() },
              { label: 'Children in', value: childrenIn.toLocaleString() },
            ]
          : []),
        { label: 'Groups', value: groups.length.toLocaleString() },
        { label: 'Walked past', value: walkedPast.toLocaleString() },
        { label: 'Turned around', value: turnedAround.toLocaleString() },
        { label: 'People', value: rows.length.toLocaleString() },
        {
          label: 'Missing footage',
          value: missingFootage.toLocaleString(),
          warn: missingFootage > 0,
        },
      ].map(({ label, value, warn }) => (
        <div
          key={label}
          className="flex flex-col gap-0.5 rounded-lg border border-amp-line bg-amp-white px-4 py-3 dark:border-amp-dark-line dark:bg-amp-dark-surface"
        >
          <p className="text-[12px] uppercase tracking-wide text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {label}
          </p>
          <p
            className={`text-xl font-semibold tabular-nums ${
              warn === true
                ? 'text-amber-700 dark:text-amber-400'
                : 'text-amp-ink dark:text-amp-dark-ink'
            }`}
          >
            {value}
          </p>
        </div>
      ))}
    </div>
  );
}

/**
 * Show only crossings outside the selected store's trading hours.
 *
 * The label carries no times, because this report is moving from one store to the
 * whole fleet and every store keeps its own hours. It previously read "before 10:00
 * or after 21:00", which was wrong for Cherry Creek (it closes at 19:00) and would
 * have been wrong for everyone else too.
 *
 * When the store has no hours recorded the control is disabled and says so, rather
 * than filtering against a guess. A wrong answer here is worse than no answer,
 * because the whole point of the filter is spotting activity that should not have
 * happened.
 */
function OutsideHoursToggle({
  checked,
  onChange,
  store,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  store: StoreHours | null;
}) {
  const known = Boolean(store?.open_time && store?.close_time);
  const hours = known ? `${store!.open_time!.slice(0, 5)} to ${store!.close_time!.slice(0, 5)}` : null;
  return (
    <label
      title={
        known
          ? `This store trades ${hours}`
          : 'This store has no trading hours recorded, so out-of-hours cannot be determined'
      }
      className={`inline-flex select-none items-center gap-2 self-end pb-1.5 text-xs ${
        known
          ? 'cursor-pointer text-amp-ink-muted dark:text-amp-dark-ink-muted'
          : 'cursor-not-allowed text-amp-ink-muted/50 dark:text-amp-dark-ink-muted/50'
      }`}
    >
      <input
        type="checkbox"
        checked={checked && known}
        disabled={!known}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-amp-olive dark:accent-amp-olive-dark"
      />
      Outside store hours only
      {known ? null : ' (hours not recorded)'}
    </label>
  );
}

/**
 * One arrival's footage: a Play button until clicked, then the player.
 *
 * The signed URL is fetched on click, one object per click, which is the whole
 * point of the button. Collapsing back to the button releases the player so a
 * long session does not accumulate a day of loaded video in the tab.
 *
 * ONE PLAYER PER ARRIVAL, not per person. The capture pads 12 seconds either side of
 * a crossing, so people a second apart produce near-identical videos and any one of
 * them shows the whole group.
 */
function FootageCell({ footage }: { footage: { clip_key: string | null; snapshot_key: string | null } }) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const key = footage.clip_key ?? footage.snapshot_key;
  const isVideo = footage.clip_key !== null;

  if (!key) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <CameraOff className="h-4 w-4" aria-hidden />
        no footage
      </span>
    );
  }

  if (url) {
    return (
      <div className="flex items-start gap-2">
        {isVideo ? (
          <video
            src={url}
            controls
            autoPlay
            playsInline
            className="h-[160px] w-auto rounded border border-amp-line bg-black dark:border-amp-dark-line"
          />
        ) : (
          <a href={url} target="_blank" rel="noopener noreferrer" title="Open full-size image">
            <img
              src={url}
              alt="Entrance still frame"
              className="h-[160px] w-auto rounded border border-amp-line object-cover hover:opacity-90 dark:border-amp-dark-line"
            />
          </a>
        )}
        <button
          type="button"
          onClick={() => setUrl(null)}
          title="Close"
          className="inline-flex items-center gap-1 rounded border border-amp-line px-1.5 py-1 text-[11px] text-amp-ink-muted hover:text-amp-ink dark:border-amp-dark-line dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
        >
          <X className="h-3 w-3" aria-hidden />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        disabled={loading}
        onClick={() => {
          setLoading(true);
          setError(null);
          fetchClipUrl(key)
            .then(setUrl)
            .catch((e: Error) => setError(e.message))
            .finally(() => setLoading(false));
        }}
        className="inline-flex items-center gap-1.5 rounded border border-amp-line bg-amp-white px-2.5 py-1.5 text-xs text-amp-ink hover:bg-amp-cream-warm disabled:opacity-60 dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
      >
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : (
          <Play className="h-3.5 w-3.5" aria-hidden />
        )}
        {isVideo ? 'Play clip' : 'View still'}
      </button>
      {error && <span className="text-[11px] text-red-700 dark:text-red-300">{error}</span>}
    </div>
  );
}

const COLUMNS = [
  'time',
  'what the counter saw',
  'adults / children (that minute)',
  'height',
  'gender',
  'age',
  'staff',
  'person id',
  'footage',
];

const CELL = 'py-2 px-3 text-xs align-middle whitespace-nowrap';
const CELL_MUTED = `${CELL} text-amp-ink-muted dark:text-amp-dark-ink-muted`;

/** "2 adults, 1 child", or a dash when the counter reported neither. */
function adultChildLabel(adults: number | null, children: number | null): string {
  if (adults === null && children === null) return '—';
  const parts: string[] = [];
  const a = adults ?? 0;
  const c = children ?? 0;
  if (a > 0) parts.push(`${a} adult${a === 1 ? '' : 's'}`);
  if (c > 0) parts.push(`${c} child${c === 1 ? '' : 'ren'}`);
  return parts.length > 0 ? parts.join(', ') : 'none';
}

/**
 * One arrival: the people who came through the door together, on one row.
 *
 * A group of one renders as an ordinary row. A group of several gets a disclosure
 * arrow, and expanding it lists the individuals with their own attributes. There is
 * one clip for the whole arrival either way.
 *
 * The adult and child cell is the counter's figure for THE WHOLE MINUTE. When the
 * arrival is the only one of its direction in that minute the figure describes exactly
 * these people and reads normally. Otherwise it is dimmed and its tooltip says it
 * covers the minute, because presenting a minute's count as this group's fact would be
 * a claim the data does not support.
 */
function GroupRow({
  group,
  expanded,
  onToggle,
}: {
  group: ArrivalGroup;
  expanded: boolean;
  onToggle: () => void;
}) {
  const many = group.people.length > 1;
  const counted = group.traffic_in > 0 || group.traffic_out > 0;
  const ids = group.people.map((p) => p.person_id).filter((id) => id > 0);
  const idLabel =
    ids.length === 0
      ? '—'
      : ids.length <= 3
        ? ids.join(', ')
        : `${ids.slice(0, 3).join(', ')} +${ids.length - 3}`;

  return (
    <>
      <tr className="border-b border-amp-line last:border-0 dark:border-amp-dark-line">
        <td className={`${CELL} text-amp-ink dark:text-amp-dark-ink tabular-nums`}>
          {group.time}
          {group.end_time !== group.time && (
            <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted"> to {group.end_time}</span>
          )}
        </td>
        <td className={CELL}>
          <div className="flex items-center gap-1.5">
            {many ? (
              <button
                type="button"
                onClick={onToggle}
                title={expanded ? 'Hide the individual people' : 'Show the individual people'}
                className="inline-flex items-center gap-1 text-amp-ink hover:text-amp-olive dark:text-amp-dark-ink dark:hover:text-amp-olive-dark"
              >
                {expanded ? (
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                )}
                <span className="font-medium">
                  {group.people.length} {crossingLabel(group.event_type).toLowerCase()}
                </span>
              </button>
            ) : (
              <span
                className={
                  counted
                    ? 'text-amp-ink dark:text-amp-dark-ink'
                    : 'text-amp-ink-muted dark:text-amp-dark-ink-muted'
                }
              >
                {crossingLabel(group.event_type)}
                {group.event_type === CROSSING.MINUTE &&
                  ` (in ${group.traffic_in}, out ${group.traffic_out})`}
              </span>
            )}
          </div>
        </td>
        <td
          className={`${CELL} ${
            group.counts_are_exact
              ? 'text-amp-ink dark:text-amp-dark-ink'
              : 'text-amp-ink-muted/70 dark:text-amp-dark-ink-muted/70'
          }`}
          title={
            group.adults === null && group.children === null
              ? 'The counter did not report an adult and child split for this minute'
              : group.counts_are_exact
                ? 'This arrival was the only one of its direction in this minute, so the split describes exactly these people'
                : 'The counter reports this split per minute, and this minute holds more than one arrival, so it covers more people than this row'
          }
        >
          {adultChildLabel(group.adults, group.children)}
        </td>
        <td className={`${CELL_MUTED} tabular-nums`}>
          {summarizeAttribute(group.people, (p) =>
            p.person_height === null ? '—' : `${p.person_height} cm`,
          )}
        </td>
        <td className={CELL_MUTED}>{summarizeAttribute(group.people, (p) => genderLabel(p.gender))}</td>
        <td className={`${CELL_MUTED} tabular-nums`}>
          {summarizeAttribute(group.people, (p) => ageLabel(p.age_min, p.age_max))}
        </td>
        <td className={CELL_MUTED}>{summarizeAttribute(group.people, (p) => staffLabel(p.is_staff))}</td>
        <td className={`${CELL_MUTED} tabular-nums`}>{idLabel}</td>
        <td className="py-2 px-3 align-middle">
          <FootageCell footage={group} />
        </td>
      </tr>

      {expanded &&
        group.people.map((p) => (
          <tr
            key={`${group.key}-${p.person_id}-${p.time}`}
            className="bg-amp-cream-warm/40 dark:bg-amp-dark-surface-warm/30"
          >
            <td className={`${CELL_MUTED} pl-8 tabular-nums`}>{p.time}</td>
            <td className={CELL_MUTED}>{crossingLabel(p.event_type)}</td>
            <td className={CELL_MUTED} />
            <td className={`${CELL_MUTED} tabular-nums`}>
              {p.person_height === null ? '—' : `${p.person_height} cm`}
            </td>
            <td className={CELL_MUTED}>{genderLabel(p.gender)}</td>
            <td className={`${CELL_MUTED} tabular-nums`}>{ageLabel(p.age_min, p.age_max)}</td>
            <td
              className={`${CELL} ${
                p.is_staff === 1
                  ? 'text-amp-olive dark:text-amp-olive-dark font-medium'
                  : 'text-amp-ink-muted dark:text-amp-dark-ink-muted'
              }`}
            >
              {staffLabel(p.is_staff)}
            </td>
            <td className={`${CELL_MUTED} tabular-nums`}>
              {p.person_id === 0 ? '—' : p.person_id.toLocaleString()}
            </td>
            {/* No per-person footage button. One clip covers the whole arrival, and
                the individuals having no clip of their own is by design. */}
            <td className={CELL_MUTED} />
          </tr>
        ))}
    </>
  );
}

export function CounterValidation() {
  const [date, setDate] = useState<string>(todayDateLocal);
  const [closedOnly, setClosedOnly] = useState(false);
  const [direction, setDirection] = useState<string>(DEFAULT_DIRECTION);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { rows: allRows, store, isLoading, error } = useStoreTrafficValidation(date);

  // Two sets on purpose: the strip describes the whole day, the table shows the
  // slice the direction filter selects.
  const dayRows = useMemo(
    () => allRows.filter((r) => (closedOnly ? isOutsideStoreHours(r.time, store) : true)),
    [allRows, closedOnly, store],
  );
  const rows = useMemo(
    () => dayRows.filter((r) => matchesDirection(r, direction)),
    [dayRows, direction],
  );
  // Grouped AFTER filtering, so a group never spans people the current filter hides
  // and the row count always matches what is on screen.
  const groups = useMemo(() => groupArrivals(rows), [rows]);
  // The strip counts arrivals over the whole day, independent of the direction filter,
  // for the same reason it counts everything else that way.
  const dayGroups = useMemo(() => groupArrivals(dayRows), [dayRows]);

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  const toolbar = (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex flex-col gap-1">
        <label
          className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted"
          htmlFor="cv-date"
        >
          Date
        </label>
        <input
          id="cv-date"
          type="date"
          value={date}
          max={todayDateLocal()}
          onChange={(e) => e.target.value && setDate(e.target.value)}
          className="rounded border border-amp-line bg-amp-white px-2.5 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label
          className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted"
          htmlFor="cv-store"
        >
          Store
        </label>
        <span
          id="cv-store"
          className="rounded border border-amp-line bg-amp-cream-warm px-2.5 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink"
        >
          {STORE_LABEL}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <label
          className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted"
          htmlFor="cv-direction"
        >
          Show
        </label>
        <select
          id="cv-direction"
          value={direction}
          onChange={(e) => setDirection(e.target.value)}
          className="rounded border border-amp-line bg-amp-white px-2.5 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          {DIRECTION_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <OutsideHoursToggle checked={closedOnly} onChange={setClosedOnly} store={store} />
    </div>
  );

  return (
    <div className="h-full flex flex-col min-h-0">

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
            IT
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">
            Counter Validation
          </h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            People who went through the door at {STORE_LABEL} within two seconds of each other,
            the same way, are shown on one row as a group, with one video for the group. Click the
            arrow on a group to see each person. Play the video to check whether the counter got
            them right.
          </p>
        </div>
        <ToolPinToggle toolSlug="counter-validation" toolName="Counter Validation" />
      </div>

      {!isLoading && !error && allRows.length > 0 && (
        <SummaryStrip rows={dayRows} groups={dayGroups} />
      )}

      <DataGridShell
        title={
          <span className="inline-flex items-center gap-2">
            <Camera className="h-4 w-4 text-amp-olive dark:text-amp-olive-dark" />
            People at the door, grouped
          </span>
        }
        loaded={isLoading ? undefined : groups.length}
        countNoun="groups"
        toolbar={toolbar}
      >
        <table className="min-w-full">
          <thead className={gridTheadClassName}>
            <tr>
              {COLUMNS.map((h) => (
                <th key={h} className="py-2 px-3 text-left text-xs font-medium">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {isLoading ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="px-4 py-6 text-center text-amp-ink-muted dark:text-amp-dark-ink-muted"
                >
                  <Loader2 className="inline h-4 w-4 animate-spin mr-2" />
                  Loading…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="px-4 py-6 text-center text-red-700 dark:text-red-300"
                >
                  Could not load validation data: {error.message}
                </td>
              </tr>
            ) : groups.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="px-4 py-6 text-center text-amp-ink-muted dark:text-amp-dark-ink-muted"
                >
                  {allRows.length === 0
                    ? 'No traffic data for this date.'
                    : 'Nobody matches the current filters.'}
                </td>
              </tr>
            ) : (
              groups.map((group) => (
                <GroupRow
                  key={group.key}
                  group={group}
                  expanded={expanded.has(group.key)}
                  onToggle={() => toggle(group.key)}
                />
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}
